// Admin trainer-management RPCs.
//
// Wire contract (confirmed live against the deployed Supabase project —
// exact parameter names, not guessed):
//   admin_list_trainer_applications(p_status)
//   admin_get_trainer_application(p_application_id)
//   admin_update_trainer_status(p_application_id, p_internal_notes, p_status)
//   admin_assign_trainer_code(p_application_id, p_code, p_discount_pct,
//     p_max_discounted_core_meals, p_minimum_core_meals, p_notes)
//
// Supabase's own admin_users check is authoritative — every RPC here
// returns "admin authorization required" (42501) for a non-admin
// caller. The frontend AdminGate is a UX convenience only.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:traineradmin]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

export async function rpcAdminListTrainerApplications(status) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer administration is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('admin_list_trainer_applications', {
      p_status: status || null,
    });
    if (error) {
      logSanitized('admin_list_trainer_applications:error', error);
      return { ok: false, data: null, error: "We couldn't load trainer applications. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('admin_list_trainer_applications:threw', err);
    return { ok: false, data: null, error: "We couldn't load trainer applications. Please try again." };
  }
}

export async function rpcAdminGetTrainerApplication(applicationId) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer administration is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('admin_get_trainer_application', {
      p_application_id: applicationId,
    });
    if (error) {
      logSanitized('admin_get_trainer_application:error', error);
      return { ok: false, data: null, error: "We couldn't load that application. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('admin_get_trainer_application:threw', err);
    return { ok: false, data: null, error: "We couldn't load that application. Please try again." };
  }
}

export async function rpcAdminUpdateTrainerStatus({ applicationId, status, internalNotes }) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer administration is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('admin_update_trainer_status', {
      p_application_id: applicationId,
      p_status: status,
      p_internal_notes: internalNotes || null,
    });
    if (error) {
      logSanitized('admin_update_trainer_status:error', error);
      return { ok: false, data: null, error: "We couldn't update that trainer's status. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('admin_update_trainer_status:threw', err);
    return { ok: false, data: null, error: "We couldn't update that trainer's status. Please try again." };
  }
}

export async function rpcAdminAssignTrainerCode({
  applicationId, code, discountPct, maxDiscountedCoreMeals, minimumCoreMeals, notes,
}) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer administration is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('admin_assign_trainer_code', {
      p_application_id: applicationId,
      p_code: code,
      p_discount_pct: discountPct,
      p_max_discounted_core_meals: maxDiscountedCoreMeals,
      p_minimum_core_meals: minimumCoreMeals,
      p_notes: notes || null,
    });
    if (error) {
      logSanitized('admin_assign_trainer_code:error', error);
      return { ok: false, data: null, error: "We couldn't assign that trainer code. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('admin_assign_trainer_code:threw', err);
    return { ok: false, data: null, error: "We couldn't assign that trainer code. Please try again." };
  }
}

// admin_list_trainer_referral_codes(p_trainer_id, p_include_expired) —
// authoritative read-back of what code(s) are actually configured for
// a trainer. React never infers validity from application status.
export async function rpcAdminListTrainerReferralCodes({ trainerId, includeExpired }) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Trainer administration is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('admin_list_trainer_referral_codes', {
      p_trainer_id: trainerId,
      p_include_expired: !!includeExpired,
    });
    if (error) {
      logSanitized('admin_list_trainer_referral_codes:error', error);
      return { ok: false, data: null, error: "We couldn't load this trainer's code history. Please try again." };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('admin_list_trainer_referral_codes:threw', err);
    return { ok: false, data: null, error: "We couldn't load this trainer's code history. Please try again." };
  }
}

// ---------------------------------------------------------------
// Tolerant readers. React never fabricates a value the RPC did not
// return, and never infers approval from the mere presence of a code.
// ---------------------------------------------------------------
function pickStr(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (typeof v === 'string' && v.length) return v; } return null; }
function pickNum(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (v == null || v === '') continue; const n = Number(v); if (Number.isFinite(n)) return n; } return null; }
function pickBool(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (typeof v === 'boolean') return v; } return null; }
function pickArr(o, k) { if (!o) return null; for (const x of k) { const v = o[x]; if (Array.isArray(v)) return v; } return null; }

export function readAdminOutcome(payload) { return pickStr(payload, ['outcome']); }

function readApplicationRow(raw) {
  return {
    application_id: pickStr(raw, ['application_id']),
    status: pickStr(raw, ['status']),
    first_name: pickStr(raw, ['first_name']),
    last_name: pickStr(raw, ['last_name']),
    email: pickStr(raw, ['email']),
    phone: pickStr(raw, ['phone']),
    business_name: pickStr(raw, ['business_name']),
    training_specialty: pickStr(raw, ['training_specialty']),
    location: pickStr(raw, ['location']),
    website_or_social: pickStr(raw, ['website_or_social']),
    bio: pickStr(raw, ['bio']),
    certification_credentials: pickStr(raw, ['certification_credentials']),
    approximate_active_clients: pickNum(raw, ['approximate_active_clients']),
    requested_trainer_code: pickStr(raw, ['requested_trainer_code']),
    current_code: pickStr(raw, ['current_code']),
    approved_code: pickStr(raw, ['approved_code']),
    current_code_valid_from: pickStr(raw, ['current_code_valid_from']),
    current_code_expires_at: pickStr(raw, ['current_code_expires_at']),
    trainer_active: pickBool(raw, ['trainer_active']),
    approved_at: pickStr(raw, ['approved_at']),
    approved_by: pickStr(raw, ['approved_by']),
    trainer_id: pickStr(raw, ['trainer_id']),
    internal_notes: pickStr(raw, ['internal_notes']),
    submitted_at: pickStr(raw, ['submitted_at', 'created_at']),
    terms_version: pickStr(raw, ['terms_version']),
    program_terms_accepted: pickBool(raw, ['program_terms_accepted']),
  };
}

// Accepts either a top-level array (Postgres SETOF) or a wrapped
// { applications | items | rows | data } shape.
export function readTrainerApplicationsList(payload) {
  let arr = null;
  if (Array.isArray(payload)) arr = payload;
  else arr = pickArr(payload, ['applications', 'items', 'rows', 'data']) || [];
  return arr.map(readApplicationRow);
}

export function readTrainerApplicationDetail(payload) {
  if (!payload) return null;
  const inner = pickStr(payload, ['outcome']) && payload.application
    ? payload.application
    : payload;
  return readApplicationRow(inner);
}

// Referral-code readback (admin_list_trainer_referral_codes). Field
// names confirmed live against the deployed project — "active" and
// "currently_valid" are distinct booleans; a code can be flagged
// active but no longer currently valid (e.g. past its expiry).
function readReferralCodeRow(raw) {
  return {
    referral_code_id: pickStr(raw, ['referral_code_id']),
    code: pickStr(raw, ['code']),
    discount_percent: pickNum(raw, ['discount_percent']),
    minimum_core_meals: pickNum(raw, ['minimum_core_meals']),
    max_discounted_core_meals: pickNum(raw, ['max_discounted_core_meals']),
    valid_from: pickStr(raw, ['valid_from']),
    expires_at: pickStr(raw, ['expires_at']),
    active: pickBool(raw, ['active']),
    currently_valid: pickBool(raw, ['currently_valid']),
    approved_at: pickStr(raw, ['approved_at']),
    approved_by: pickStr(raw, ['approved_by']),
    revoked_at: pickStr(raw, ['revoked_at']),
    revoked_by: pickStr(raw, ['revoked_by']),
    notes: pickStr(raw, ['notes']),
    trainer_name: pickStr(raw, ['trainer_name']),
  };
}

export function readTrainerReferralCodesOutcome(payload) { return pickStr(payload, ['outcome']); }

export function readTrainerReferralCodesList(payload) {
  const arr = pickArr(payload, ['codes', 'items', 'rows', 'data']) || [];
  return arr.map(readReferralCodeRow);
}
